import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { FrameOrderFilters as FiltersType, FrameOrderFilterOptions } from '../../types/frameOrders';

interface FrameOrderFiltersProps {
    filters: FiltersType;
    filterOptions: FrameOrderFilterOptions | null;
    onFilterChange: (filters: FiltersType) => void;
}

export function FrameOrderFilters({ filters, filterOptions, onFilterChange }: FrameOrderFiltersProps) {
    const [searchInput, setSearchInput] = useState(filters.search || '');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== (filters.search || '')) {
                onFilterChange({ ...filters, search: searchInput || undefined });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleSelectChange = (key: keyof FiltersType, value: string) => {
        const updated = { ...filters, [key]: value || undefined };
        // Clear classificationLabel when dropdown is used (they are mutually exclusive)
        if (key === 'classificationCode') {
            delete updated.classificationLabel;
        }
        onFilterChange(updated);
    };

    const clearFilters = () => {
        setSearchInput('');
        onFilterChange({});
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== undefined && v !== '');

    const selectClass = "h-9 rounded-md border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

    return (
        <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search customer, product, document..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    className="h-9 w-64 rounded-md border border-gray-300 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>

            <select
                value={filters.companyCode || ''}
                onChange={e => handleSelectChange('companyCode', e.target.value)}
                className={selectClass}
            >
                <option value="">All Companies</option>
                {filterOptions?.companies.map(c => (
                    <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                ))}
            </select>

            <select
                value={filters.documentType || ''}
                onChange={e => handleSelectChange('documentType', e.target.value)}
                className={selectClass}
            >
                <option value="">All Types</option>
                {filterOptions?.documentTypes.map(d => (
                    <option key={d} value={d}>{d}</option>
                ))}
            </select>

            <select
                value={filters.classificationCode || ''}
                onChange={e => handleSelectChange('classificationCode', e.target.value)}
                className={selectClass}
            >
                <option value="">All Classifications</option>
                {filterOptions?.classifications.map((c, i) => (
                    <option key={`${c.code}-${i}`} value={String(c.code)}>{c.label}</option>
                ))}
            </select>

            <select
                value={filters.salesTeam || ''}
                onChange={e => handleSelectChange('salesTeam', e.target.value)}
                className={selectClass}
            >
                <option value="">All Sales Teams</option>
                {filterOptions?.salesTeams.map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>

            <select
                value={filters.salesManager || ''}
                onChange={e => handleSelectChange('salesManager', e.target.value)}
                className={selectClass}
            >
                <option value="">All Sales Managers</option>
                {filterOptions?.salesManagers.map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>

            <select
                value={filters.salesCoordinator || ''}
                onChange={e => handleSelectChange('salesCoordinator', e.target.value)}
                className={selectClass}
            >
                <option value="">All Coordinators</option>
                {filterOptions?.salesCoordinators.map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>

            {hasActiveFilters && (
                <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                    <X className="h-3 w-3" />
                    Clear
                </button>
            )}

            {filters.classificationLabel && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                    Classification: {filters.classificationLabel}
                    <button
                        onClick={() => onFilterChange({ ...filters, classificationLabel: undefined })}
                        className="rounded-full p-0.5 hover:bg-blue-200"
                        title="Remove classification filter"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            )}

            {filters.documentId && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                    Filtered: {filters.documentId}
                    <button
                        onClick={() => onFilterChange({ ...filters, documentId: undefined })}
                        className="rounded-full p-0.5 hover:bg-orange-200"
                        title="Remove document filter"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            )}
        </div>
    );
}
